import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Store, Package, Menu, ShoppingBag } from "lucide-react";
import PhoneOtpModal from "../auth/PhoneOtpModal";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

/**
 * MobileBottomNav — fixed bottom tab bar shown on phones AND tablets
 * (`lg:hidden` — hides at ≥1024px so desktop keeps the full header).
 *
 * Tabs:
 *   - Home        -> /
 *   - Shop        -> /shop
 *   - My Orders   -> /account?tab=orders (dimmed + opens sign-in modal when logged out)
 *   - More        -> dispatches a global event so StoreHeader's hamburger Sheet opens
 *
 * A floating cart pill sits just above the bar, surfacing the live item
 * count. Both surfaces gain `safe-area-inset-bottom` so they sit cleanly
 * above the iOS home indicator inside the Capacitor wrapper.
 */
const TABS = [
  { key: "home",   label: "Home",      icon: Home,    to: "/",     match: (p) => p === "/" },
  { key: "shop",   label: "Shop",      icon: Store,   to: "/shop", match: (p) => p === "/shop" || p.startsWith("/shop/") },
  { key: "orders", label: "My Orders", icon: Package, to: "/account?tab=orders",
    match: (p, s) => p === "/account" && (s.get("tab") === "orders" || !s.get("tab")) },
  { key: "more",   label: "More",      icon: Menu,    isAction: true },
];

export default function MobileBottomNav() {
  const { user } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const [authOpen, setAuthOpen] = useState(false);

  const handleClick = (tab, e) => {
    if (tab.isAction) {
      // Ask StoreHeader to open its hamburger Sheet via a window event.
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("storefront:open-mobile-menu"));
      return;
    }
    if (tab.key === "orders" && !user) {
      e.preventDefault();
      setAuthOpen(true);
    }
  };

  return (
    <>
      {/* Floating cart pill — sits just above the tab bar */}
      <Link
        to="/cart"
        className="lg:hidden fixed right-4 z-40 flex items-center gap-2 bg-[hsl(var(--kb-primary))] text-white rounded-full px-4 py-3 shadow-lg active:scale-95 transition-transform"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 4.5rem)" }}
        data-testid="mobile-floating-cart"
        aria-label={`Cart, ${cartCount} item${cartCount === 1 ? "" : "s"}`}
      >
        <ShoppingBag size={18} />
        {cartCount > 0 && (
          <span
            className="text-xs font-medium tabular-nums"
            data-testid="mobile-floating-cart-count"
          >
            {cartCount}
          </span>
        )}
      </Link>

      {/* Tab bar */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[hsl(var(--kb-border))] shadow-[0_-4px_16px_rgba(0,0,0,0.05)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isOrders = tab.key === "orders";
            const dimmed = isOrders && !user;
            const isActive = !tab.isAction && tab.match(location.pathname, search);
            const className = [
              "flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] uppercase tracking-wider",
              "transition-colors",
              isActive ? "text-[hsl(var(--kb-primary))]" : "text-[hsl(var(--kb-muted))]",
              dimmed ? "opacity-50" : "",
            ].join(" ");

            if (tab.isAction) {
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={(e) => handleClick(tab, e)}
                  className={className}
                  data-testid={`mobile-tab-${tab.key}`}
                >
                  <Icon size={20} aria-hidden="true" />
                  <span>{tab.label}</span>
                </button>
              );
            }
            return (
              <NavLink
                key={tab.key}
                to={tab.to}
                onClick={(e) => handleClick(tab, e)}
                className={className}
                data-testid={`mobile-tab-${tab.key}`}
              >
                <Icon size={20} aria-hidden="true" />
                <span>{tab.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <PhoneOtpModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
